import { ReactNode, useEffect, useId, useRef, useState } from "react";

import { Checkbox, Field, Input } from "@/components/form";
import { ErrorBox } from "@/components/Spinner";
import { Button, Notice, TONE_SURFACE } from "@/components/ui";

/**
 * The dialogs that are open, oldest first. Only the LAST one is live: a confirmation can open over
 * another — the one that asks whether to leave a form with unsaved changes opens over whatever the
 * form had open (the production-key prompt of a demotion, say), and two dialogs trapping the same keys
 * would fight (Escape would close both, Tab would be pulled into each in turn) while a screen reader
 * would see two modal dialogs. So the last one answers to Escape and Tab, and every one under it is
 * `inert`: not focusable, not clickable, and out of the accessibility tree, as the page is behind a dialog.
 */
interface OpenDialog {
  id: symbol;
  root: () => HTMLElement | null;
}
const openDialogs: OpenDialog[] = [];

/**
 * Make the last dialog live and the ones under it inert. Called by hand, in the same breath as the push
 * or the pop of the stack, and not from a render: when the dialog on top closes it gives the focus back
 * to what had it in the one below, and that only works if the one below is no longer inert at that instant.
 */
function syncInert() {
  openDialogs.forEach((dialog, index) => {
    const root = dialog.root();
    if (!root) return;
    if (index === openDialogs.length - 1) root.removeAttribute("inert");
    else root.setAttribute("inert", "");
  });
}

interface ConfirmModalProps {
  title: string;
  /** Body content, in business language. */
  children: ReactNode;
  confirmLabel: string;
  /** What the way out says. "Cancel" by default; a question that is not about cancelling something says its own ("Stay"). */
  cancelLabel?: string;
  pendingLabel?: string;
  pending?: boolean;
  /** Red treatment for destructive actions; neutral otherwise. */
  danger?: boolean;
  /**
   * When set, the operator must type this exact phrase to unlock the confirm
   * button (irreversible actions). Omit for a plain confirm.
   */
  confirmPhrase?: string;
  /**
   * When set, an extra acknowledgement checkbox must be ticked before the
   * confirm button unlocks (e.g. production safety).
   */
  acknowledgement?: ReactNode;
  /**
   * When true, the operator must paste the production key. It is never
   * stored, never pre-filled, and is handed to onConfirm for this call only.
   */
  productionKey?: boolean;
  /**
   * The failed mutation's error, if any. Rendered inside the modal body —
   * the confirm/cancel overlay sits above everything else on the page, so an
   * ``ErrorBox`` left there is invisible while the modal that caused it is
   * still open. A wrong production key is the EXPECTED failure mode of these
   * modals; the operator needs to see why without hunting behind the dialog.
   */
  error?: unknown;
  /**
   * The action can no longer be confirmed: what it was about changed while the modal was open (a run
   * that finished by itself). The button is locked and the body says why; Cancel closes.
   */
  confirmDisabled?: boolean;
  /**
   * How the error is said. `ErrorBox` by default; a refusal that has something
   * specific to explain (which tenants block a delete) draws it here, so it keeps
   * the same place, the same scroll into view and the same `errorHint` under it.
   */
  errorView?: (error: unknown) => ReactNode;
  /**
   * A pointer to the way out of THIS refusal, shown right under the error and
   * only while there is one (e.g. "use Force delete" when a delete is refused
   * for its deployment history). It lives in the modal, like the error, so
   * both go together on Cancel: a hint left on the page would outlive the
   * error it explains.
   */
  errorHint?: ReactNode;
  onCancel: () => void;
  onConfirm: (productionKey?: string) => void;
}

/**
 * Single confirmation surface for every destructive / irreversible action in
 * the app. Replaces the browser's own confirmation dialogs (which block the
 * event loop) and the two bespoke destroy modals. Supports an optional
 * type-to-confirm gate and an optional acknowledgement checkbox so one
 * component covers the whole spectrum from "Archive?" to "Force delete prod".
 */
export function ConfirmModal({
  title,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  pendingLabel,
  pending = false,
  danger = false,
  confirmPhrase,
  acknowledgement,
  productionKey,
  error,
  confirmDisabled = false,
  errorView,
  errorHint,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);
  const [key, setKey] = useState("");

  // What had the focus when the modal was asked for — the button or the menu
  // trigger that opened it. It must be read while RENDERING, before anything
  // here takes the focus: a type-to-confirm input autoFocuses itself during the
  // commit, so by the time an effect ran, `document.activeElement` was that
  // input, which is gone when the modal closes and the focus fell on <body>.
  const [opener] = useState(() => document.activeElement as HTMLElement | null);

  // Where this dialog stands among the open ones (see ``openDialogs``). Declared BEFORE the effect that
  // moves the focus: the cleanups of a closing dialog run in the order they are declared, and this one
  // must make the dialog below live again before the other gives it back the focus.
  const overlayRef = useRef<HTMLDivElement>(null);
  const [self] = useState(() => Symbol("dialog"));
  useEffect(() => {
    openDialogs.push({ id: self, root: () => overlayRef.current });
    syncInert();
    return () => {
      const at = openDialogs.findIndex((dialog) => dialog.id === self);
      if (at >= 0) openDialogs.splice(at, 1);
      syncInert();
    };
  }, [self]);

  // Land focus inside the dialog on open so keyboard + screen-reader users
  // aren't stranded on the page behind the overlay. When a type-to-confirm
  // input is present it autoFocuses itself; otherwise we focus the dialog
  // container (tabIndex -1). On close, return focus to whatever opened the
  // modal so the keyboard user resumes where they left off.
  //
  // Only when the dialog is REALLY gone. In development, StrictMode runs this
  // cleanup once right after mounting (it simulates an unmount to shake out
  // effects that do not clean up): with the dialog still in the page, that
  // would pull the focus off the type-to-confirm input and back onto the
  // button behind the overlay, and what the operator types would go nowhere.
  useEffect(() => {
    if (!confirmPhrase) dialogRef.current?.focus();
    return () => {
      if (!dialogRef.current?.isConnected) opener?.focus?.();
    };
    // Mount-only: confirmPhrase is fixed for a given modal instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The error is rendered ABOVE the fields, inside the scrolling body. With a
  // short or zoomed window and the operator scrolled down to the production-key
  // field, a refusal would appear out of sight (scroll anchoring keeps the view
  // where it was) and the click would seem to do nothing.
  useEffect(() => {
    if (error != null) errorRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [error]);

  // Esc closes; Tab is trapped within the dialog so focus can't escape to the
  // inert page underneath.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // A dialog opened over this one owns the keys until it closes.
      if (openDialogs[openDialogs.length - 1]?.id !== self) return;
      if (e.key === "Escape" && !pending) {
        onCancel();
        return;
      }
      const dialog = dialogRef.current;
      if (e.key !== "Tab" || !dialog) return;
      // ``summary`` matters: the "Technical details" disclosure of an ErrorBox
      // rendered in the body is a tab stop, and leaving it out lets Shift+Tab
      // from it slip past the dialog onto the page underneath.
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      );
      // Every control is disabled (a request is pending): hold focus on the
      // dialog itself rather than letting Tab walk onto the page behind.
      if (focusable.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      // Focus already outside the dialog — typically on <body>, after the
      // button that held it became disabled: bring it back in.
      if (!active || !dialog.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      // The dialog container takes focus on open (no type-to-confirm field) and
      // is not in the list above: Shift+Tab from it must wrap like from ``first``.
      if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, pending, self]);

  const phraseOk = !confirmPhrase || typed === confirmPhrase;
  const ackOk = !acknowledgement || ack;
  const keyOk = !productionKey || key.length > 0;
  const canSubmit = phraseOk && ackOk && keyOk && !pending && !confirmDisabled;

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-inverse/50 px-4 backdrop-blur-xs"
      onClick={() => !pending && onCancel()}
    >
      {/* Capped at the viewport: header and footer stay pinned and only the
          body scrolls, so Cancel and Confirm are always reachable — a long
          list of blocking tenants or a tall error would otherwise push them
          off-screen with no way to scroll the fixed overlay. */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        // ``outline-hidden``, unconditional: this container only takes focus from
        // script (see the effect above) and is not a control, so the global
        // focus ring has no business drawing a frame around the whole dialog.
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-lg bg-surface shadow-xl outline-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className={
            danger
              ? `shrink-0 rounded-t-lg border-b px-6 py-4 ${TONE_SURFACE.danger}`
              : "shrink-0 rounded-t-lg border-b border-line bg-sunken px-6 py-4"
          }
        >
          <h2
            id={titleId}
            className={
              danger
                ? "text-lg font-semibold text-danger-fg"
                : "text-lg font-semibold text-ink"
            }
          >
            {title}
          </h2>
        </header>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5 text-sm text-ink">
          {children}

          {error != null && (
            <div ref={errorRef} className="space-y-2">
              {errorView ? errorView(error) : <ErrorBox error={error} />}
              {errorHint}
            </div>
          )}

          {confirmPhrase && (
            <Field
              label={
                <>
                  Type <code className="font-mono">{confirmPhrase}</code> to
                  confirm
                </>
              }
            >
              <Input
                mono
                type="text"
                autoComplete="off"
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={confirmPhrase}
                disabled={pending}
              />
            </Field>
          )}

          {acknowledgement && (
            <Notice tone="warning">
              <Checkbox
                label={acknowledgement}
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                disabled={pending}
              />
            </Notice>
          )}

          {productionKey && (
            <Field
              label="Production key"
              help="Required for production targets. It is never stored in this browser."
            >
              <Input
                mono
                type="password"
                autoComplete="off"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Paste it from your password manager"
                disabled={pending}
              />
            </Field>
          )}
        </div>

        <footer className="flex shrink-0 items-center justify-end gap-2 rounded-b-lg border-t border-line bg-sunken px-6 py-3">
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? "danger" : "primary"}
            loading={pending}
            disabled={!canSubmit}
            onClick={() => onConfirm(productionKey ? key : undefined)}
          >
            {pending ? (pendingLabel ?? "Processing…") : confirmLabel}
          </Button>
        </footer>
      </div>
    </div>
  );
}
